import type { Node } from "@tiptap/pm/model";

// Spreadsheet quoting (Excel, Google Sheets, Numbers): a cell holding a tab, a line break or a quote is
// wrapped in quotes, and its own quotes are doubled.
const NEEDS_QUOTES = /[\t\r\n"]/;

const quoteCell = (text: string) => (NEEDS_QUOTES.test(text) ? `"${text.replace(/"/g, '""')}"` : text);

export const tableToTsv = (table: Node): string => {
	const rows: string[] = [];
	table.forEach((row) => {
		const cells: string[] = [];
		row.forEach((cell) => cells.push(quoteCell(cell.textBetween(0, cell.content.size, "\n"))));
		rows.push(cells.join("\t"));
	});
	return rows.join("\n");
};

const readQuotedCell = (text: string, open: number): { cell: string; end: number } | null => {
	let cell = "";
	let i = open + 1;
	while (i < text.length) {
		if (text[i] !== '"') cell += text[i++];
		else if (text[i + 1] === '"') {
			cell += '"';
			i += 2;
		} else return { cell, end: i + 1 };
	}
	return null;
};

const splitTsv = (text: string): string[][] | null => {
	const rows: string[][] = [];
	let row: string[] = [];
	let cell = "";
	let i = 0;

	while (i <= text.length) {
		if (text[i] === '"' && cell === "") {
			const quoted = readQuotedCell(text, i);
			if (!quoted || (quoted.end < text.length && !"\t\r\n".includes(text[quoted.end]))) return null;
			cell = quoted.cell;
			i = quoted.end;
			continue;
		}

		const char = text[i];
		if (char === "\t") {
			row.push(cell);
			cell = "";
		} else if (char === "\r" || char === "\n" || char === undefined) {
			row.push(cell);
			rows.push(row);
			row = [];
			cell = "";
			if (char === "\r" && text[i + 1] === "\n") i++;
		} else cell += char;
		i++;
	}

	return rows;
};

/**
 * Rows of cells when the text is a tab-separated table, otherwise null.
 *
 * Needs at least two rows of the same width, two columns or more; a text whose every line starts with a tab
 * is indented code or prose, not a table.
 */
export const parseTsvTable = (text: string): string[][] | null => {
	if (!text?.includes("\t")) return null;

	const rows = splitTsv(text.replace(/\r?\n$/, ""));
	if (!rows || rows.length < 2) return null;

	const width = rows[0].length;
	if (width < 2 || rows.some((row) => row.length !== width)) return null;
	if (rows.every((row) => row[0] === "")) return null;

	return rows;
};
